import {
  DEFAULT_BOARD_NORMALIZED,
  DEFAULT_PIN_NORMALIZED,
  infer_capability_tags,
  infer_electrical_type,
  infer_pin_role,
  infer_pin_voltage,
  infer_supported_protocols,
  normalize_booleanish,
  normalize_pin_role_value,
  normalize_string_array,
  parse_nullable_number,
} from './pinInference';
import { normalizeSupportedProtocols } from './compatibilityOptions';
import { find_board_profile, normalize_profile_pin_role } from './boardProfiles';
import { getPinAlternateNames } from './pinAlternateNames';

const get_board_pins = (board) => {
  const candidates = [
    board?.pins,
    board?.board_pins,
    board?.boardPins,
    board?.pinout,
  ];

  return candidates.find(Array.isArray) || [];
};

const dedupe_array = (items) => {
  return Array.from(new Set(items.filter(Boolean)));
};

const get_profile_pin_override = (profile, pin) => {
  if (!profile?.pin_overrides) {
    return null;
  }

  const pin_name = String(pin?.pin_name || pin?.pinName || '').trim().toLowerCase();
  const pin_number = String(pin?.pin_number || pin?.pinNumber || '').trim().toLowerCase();
  const alternate_names = getPinAlternateNames(pin)
    .map((name) => String(name).trim().toLowerCase());

  const keys = [pin_name, pin_number, ...alternate_names].filter(Boolean);

  for (const key of keys) {
    if (profile.pin_overrides[key]) {
      return profile.pin_overrides[key];
    }
  }

  return null;
};

const get_role_result = (raw_pin) => {
  const explicit_role = normalize_pin_role_value(raw_pin?.pin_role || raw_pin?.pinRole);

  if (!explicit_role) {
    return infer_pin_role(raw_pin);
  }

  if (explicit_role === 'power-input-output') {
    const inferred_role = infer_pin_role(raw_pin);

    if (['power-input', 'power-output'].includes(inferred_role.value)) {
      return inferred_role;
    }
  }

  return {
    value: explicit_role,
    confidence: 'high',
    source: 'explicit',
  };
};

const normalize_pin = (raw_pin, board) => {
  const role_result = get_role_result(raw_pin);
  const explicit_tags = normalize_string_array(
    raw_pin?.capability_tags || raw_pin?.capabilityTags
  );
  const inferred_tags = infer_capability_tags({
    ...raw_pin,
    pin_role: role_result.value,
  });
  const capability_tags = dedupe_array([
    ...explicit_tags.map((tag) => normalize_pin_role_value(tag) || String(tag).trim().toLowerCase()),
    ...inferred_tags,
  ]);
  const explicit_voltage_min = parse_nullable_number(raw_pin?.voltage_min_v || raw_pin?.voltageMinV);
  const explicit_voltage_max = parse_nullable_number(raw_pin?.voltage_max_v || raw_pin?.voltageMaxV);
  const inferred_voltage = infer_pin_voltage(
    {
      ...raw_pin,
      pin_role: role_result.value,
      capability_tags,
    },
    board
  );
  const electrical_type = raw_pin?.electrical_type
    ? String(raw_pin.electrical_type).trim()
    : infer_electrical_type({
        ...raw_pin,
        pin_role: role_result.value,
        capability_tags,
      });

  return {
    ...DEFAULT_PIN_NORMALIZED,
    ...raw_pin,
    pin_name: String(raw_pin?.pin_name || raw_pin?.pinName || raw_pin?.name || '').trim(),
    pin_number: String(raw_pin?.pin_number || raw_pin?.pinNumber || '').trim(),
    functions: String(raw_pin?.functions || '').trim(),
    pin_role: role_result.value,
    capability_tags,
    electrical_type,
    voltage_min_v: explicit_voltage_min !== null ? explicit_voltage_min : inferred_voltage.min,
    voltage_max_v: explicit_voltage_max !== null ? explicit_voltage_max : inferred_voltage.max,
    current_limit_ma: parse_nullable_number(raw_pin?.current_limit_ma || raw_pin?.currentLimitMa || raw_pin?.current_limit || raw_pin?.currentLimit),
    is_5v_tolerant: normalize_booleanish(raw_pin?.is_5v_tolerant ?? raw_pin?.is5vTolerant),
    restrictions: normalize_string_array(raw_pin?.restrictions),
    safe_usage_notes: normalize_string_array(raw_pin?.safe_usage_notes || raw_pin?.safeUsageNotes),
    alternate_pin_names: raw_pin?.alternate_pin_names ?? raw_pin?.alternatePinNames ?? [],
    inference: {
      pin_role_source: role_result.source,
      pin_role_confidence: role_result.confidence,
      capability_tags_source: explicit_tags.length > 0 ? 'explicit' : 'inferred',
      capability_tags_confidence: explicit_tags.length > 0 ? 'high' : role_result.confidence,
      voltage_source:
        explicit_voltage_min !== null || explicit_voltage_max !== null
          ? 'explicit'
          : inferred_voltage.source,
      voltage_confidence:
        explicit_voltage_min !== null || explicit_voltage_max !== null
          ? 'high'
          : inferred_voltage.confidence,
      electrical_type_source: raw_pin?.electrical_type ? 'explicit' : 'inferred',
      electrical_type_confidence: raw_pin?.electrical_type ? 'high' : role_result.confidence,
      confidence: role_result.confidence,
    },
  };
};

const apply_profile_to_pin = (pin, override, profile) => {
  if (!override) {
    return pin;
  }

  const next_pin = {
    ...pin,
    ...override,
    pin_role: normalize_profile_pin_role(override.pin_role) || pin.pin_role,
    capability_tags: dedupe_array([
      ...pin.capability_tags,
      ...normalize_string_array(override.capability_tags),
    ]),
    restrictions: dedupe_array([
      ...pin.restrictions,
      ...normalize_string_array(override.restrictions),
    ]),
    safe_usage_notes: dedupe_array([
      ...pin.safe_usage_notes,
      ...normalize_string_array(override.safe_usage_notes),
    ]),
    voltage_min_v:
      Object.prototype.hasOwnProperty.call(override, 'voltage_min_v')
        ? parse_nullable_number(override.voltage_min_v)
        : pin.voltage_min_v,
    voltage_max_v:
      Object.prototype.hasOwnProperty.call(override, 'voltage_max_v')
        ? parse_nullable_number(override.voltage_max_v)
        : pin.voltage_max_v,
    current_limit_ma:
      Object.prototype.hasOwnProperty.call(override, 'current_limit_ma')
        ? parse_nullable_number(override.current_limit_ma)
        : pin.current_limit_ma,
    is_5v_tolerant:
      Object.prototype.hasOwnProperty.call(override, 'is_5v_tolerant')
        ? normalize_booleanish(override.is_5v_tolerant)
        : pin.is_5v_tolerant,
  };

  next_pin.electrical_type = override.electrical_type || infer_electrical_type(next_pin);
  next_pin.inference = {
    ...pin.inference,
    profile_applied: true,
    profile_source: profile.id,
    pin_role_source: override.pin_role ? 'profile' : pin.inference.pin_role_source,
    pin_role_confidence: override.pin_role ? 'high' : pin.inference.pin_role_confidence,
    capability_tags_source: override.capability_tags ? 'profile' : pin.inference.capability_tags_source,
    capability_tags_confidence: override.capability_tags ? 'high' : pin.inference.capability_tags_confidence,
    voltage_source:
      Object.prototype.hasOwnProperty.call(override, 'voltage_min_v') ||
      Object.prototype.hasOwnProperty.call(override, 'voltage_max_v')
        ? 'profile'
        : pin.inference.voltage_source,
    voltage_confidence:
      Object.prototype.hasOwnProperty.call(override, 'voltage_min_v') ||
      Object.prototype.hasOwnProperty.call(override, 'voltage_max_v')
        ? 'high'
        : pin.inference.voltage_confidence,
    electrical_type_source: override.electrical_type ? 'profile' : pin.inference.electrical_type_source,
    electrical_type_confidence: override.electrical_type ? 'high' : pin.inference.electrical_type_confidence,
  };

  return next_pin;
};

const get_board_analysis_confidence = (pins, profile) => {
  if (!pins.length) {
    return 'low';
  }

  const high_confidence_pin_count = pins.filter((pin) =>
    ['explicit', 'profile'].includes(pin?.inference?.pin_role_source)
  ).length;

  if (profile && high_confidence_pin_count / pins.length >= 0.5) {
    return 'high';
  }

  if (high_confidence_pin_count / pins.length >= 0.3) {
    return 'medium';
  }

  return 'low';
};

export const normalize_board = (raw_board, raw_pins = []) => {
  const base_board = {
    ...DEFAULT_BOARD_NORMALIZED,
    ...raw_board,
    supply_voltage_min_v: parse_nullable_number(raw_board?.supply_voltage_min_v),
    supply_voltage_max_v: parse_nullable_number(raw_board?.supply_voltage_max_v),
    logic_voltage_min_v: parse_nullable_number(raw_board?.logic_voltage_min_v),
    logic_voltage_max_v: parse_nullable_number(raw_board?.logic_voltage_max_v),
    current_draw_typ_ma: parse_nullable_number(raw_board?.current_draw_typ_ma),
    current_draw_max_ma: parse_nullable_number(raw_board?.current_draw_max_ma),
    rail_3v3_max_ma: parse_nullable_number(raw_board?.rail_3v3_max_ma),
    rail_5v_max_ma: parse_nullable_number(raw_board?.rail_5v_max_ma),
    supported_protocols: normalizeSupportedProtocols(raw_board?.supported_protocols),
  };

  const profile = find_board_profile(base_board);
  const board_with_profile = profile
    ? {
        ...base_board,
        ...profile.board_overrides,
        supported_protocols: dedupe_array([
          ...base_board.supported_protocols,
          ...normalizeSupportedProtocols(profile.board_overrides?.supported_protocols),
        ]),
      }
    : base_board;
  const pins_to_normalize = Array.isArray(raw_pins) ? raw_pins : get_board_pins(raw_board);
  const normalized_pins = pins_to_normalize.map((pin) => normalize_pin(pin, board_with_profile));
  const profiled_pins = normalized_pins.map((pin) =>
    apply_profile_to_pin(pin, get_profile_pin_override(profile, pin), profile)
  );
  const supported_protocols = dedupe_array([
    ...board_with_profile.supported_protocols,
    ...infer_supported_protocols(board_with_profile, profiled_pins),
  ]);

  return {
    ...board_with_profile,
    pins: profiled_pins,
    supported_protocols,
    analysis_confidence:
      board_with_profile.analysis_confidence ||
      get_board_analysis_confidence(profiled_pins, profile),
    profile_id: profile?.id || '',
  };
};

export const normalize_boards_collection = (boards = []) => {
  return boards.map((board) => normalize_board(board, get_board_pins(board)));
};

export const get_normalized_board_pins = (board) => {
  return get_board_pins(board);
};
